import React, { useRef, useEffect, useState } from 'react';
import { IoInformationCircleOutline } from 'react-icons/io5';
import PunchPointsModal from './PunchPointsModal';
import PropTypes from 'prop-types';
import { Form, Button, Row, Col, Table, Modal, Alert } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useWatch } from 'react-hook-form';
import SignatureCanvas from 'react-signature-canvas';
import { SUBMIT_FORM, VIEW_FORM } from 'actions/formActions';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { cookie } from 'utils/cookies';

const CableTrayForm = ({ role = cookie.get('user-role'), userId = cookie.get('user-id') }) => {
  const dispatch = useDispatch();
  const { viewFormData } = useSelector((state) => state.checklist);
  const { formId, stationId } = useParams();
  const navigate = useNavigate();

  const sigCanvas = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [showPunchModal, setShowPunchModal] = useState(false);
  const [pendingRework, setPendingRework] = useState(null);
  const [currentSignature, setCurrentSignature] = useState({ role: '' });
  const [isLoading, setIsLoading] = useState(true);
  const dataLoadedRef = useRef(false);

  const { register, control, handleSubmit, setValue, getValues } = useForm({
    defaultValues: {
      // basics
      station_id: stationId || '',
      project_name: '',
      cabel_tray_size: '',
      no_of_trays: '',
      cabel_tray_material: '',
      cabel_tray_type: '',

      // checks
      visual_checks: Array(13).fill({ status: '', remark: '' }),

      // text areas
      other_comments: '',
      observations: { a: '', b: '', c: '' },

      // signatures/comments (single level)
      comments: {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      },

      // single-level workflow status
      status: 'draft',
      manager_approve: false, // parity with APFC (optional flag)
    },
  });

  const formValues = useWatch({ control });
  const punch_points = useWatch({ control, name: 'punch_points' });
  const statusWatch = useWatch({ control, name: 'status' });
  const manager_id = useWatch({ control, name: 'manager_id' });

  // ---------- Autosave (single-level) ----------
  const autoSave = React.useCallback(
    _.debounce((data) => {
      if (!dataLoadedRef.current) return;
      const payload = {
        form_data: {
          status: data.status,
          form_id: formId,
          formName: 'cabel_tray', // keep spelling to match backend
          station_id: data.station_id,
          project_name: data.project_name,
          cabel_tray_size: data.cabel_tray_size,
          no_of_trays: data.no_of_trays,
          cabel_tray_material: data.cabel_tray_material,
          cabel_tray_type: data.cabel_tray_type,
          visual_checks: data.visual_checks,
          other_comments: data.other_comments,
          comments: data.comments,
          observations: data.observations,
          manager_approve: data.manager_approve,
          punch_points: data.punch_points,
        },
      };
      dispatch({ type: SUBMIT_FORM, payload: { data: payload.form_data } });
    }, 2000),
    [dispatch, formId]
  );

  useEffect(() => {
    autoSave(formValues);
  }, [formValues, autoSave]);

  // ---------- Load existing ----------
  useEffect(() => {
    if (formId) {
      dispatch({ type: VIEW_FORM, payload: { formId, formName: 'cabel_tray' } });
    }
    setIsLoading(false);
  }, [dispatch, formId]);

  useEffect(() => {
    if (viewFormData) {
      const d = viewFormData || {};
      setValue('station_id', d.station_id || stationId || '');
      setValue('project_name', d.project_name || '');
      setValue('cabel_tray_size', d.cabel_tray_size || '');
      setValue('no_of_trays', d.no_of_trays || '');
      setValue('cabel_tray_material', d.cabel_tray_material || '');
      setValue('cabel_tray_type', d.cabel_tray_type || '');
      setValue(
        'visual_checks',
        Array.isArray(d.visual_checks) ? d.visual_checks : Array(13).fill({ status: '', remark: '' })
      );
      setValue('other_comments', d.other_comments || '');
      setValue(
        'comments',
        d.comments || {
          contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
          charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
          quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
          optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        }
      );
      setValue('observations', d.observations || { a: '', b: '', c: '' });
      setValue('status', d.status || 'draft');
      setValue('manager_approve', d.manager_approve || false);
      setValue('manager_id', d.l1?.managerId || d.managerId || null);
      setValue('punch_points', viewFormData?.punch_points || '');
      dataLoadedRef.current = true;
    }
  }, [viewFormData, stationId, setValue, role]);

  // ---------- Role/Stage helpers ----------
  const status = () => getValues('status');

  const fieldsEditable =
    ((!manager_id || manager_id === userId) && ((role === 'engineer' && (statusWatch === 'draft' || statusWatch === 'CONTRACTOR' || statusWatch === 'MANAGER')) || (role === 'contractor' && (statusWatch === 'draft' || statusWatch === 'CONTRACTOR'))));

  const canComment =
    (role === 'engineer' && (status() === 'draft' || status() === 'CONTRACTOR')) ||
    (role === 'contractor' && status() === 'CONTRACTOR') ||
    (role === 'manager' && status() === 'MANAGER') ||
    (role === 'quality' && status() === 'QUALITY');

  const isSigEditable = (sigRole) => {
    if (role !== 'quality' && manager_id && manager_id !== userId) return false;
    return (
      (role === 'engineer' && sigRole === 'charge_engineer' && (status() === 'draft' || status() === 'CONTRACTOR' || status() === 'MANAGER')) ||
      (role === 'contractor' && sigRole === 'contractor' && (status() === 'draft' || status() === 'CONTRACTOR')) ||
      (role === 'quality' && sigRole === 'quality' && status() === 'QUALITY')
    );
  };

  // ---------- Signature handlers ----------
  const handleImageUpload = (e, sigRole) => {
    const file = e.target.files[0];
    const maxSizeInBytes = 0.05 * 1024 * 1024; // 50KB
    if (file) {
      if (file.size > maxSizeInBytes) {
        alert('File size exceeds 50kb. Please upload a smaller image.');
        handleResetSignature(sigRole);
        sigCanvas.current?.clear();
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setValue(`comments.${sigRole}.signature`, reader.result, { shouldDirty: true, shouldValidate: true });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSignatureTypeChange = (sigRole, type) => {
    setValue(`comments.${sigRole}.signatureType`, type, { shouldDirty: true, shouldValidate: true });
    setValue(`comments.${sigRole}.signature`, '', { shouldDirty: true, shouldValidate: true });
    if (type === 'digital') {
      setCurrentSignature({ role: sigRole });
      setShowModal(true);
    }
  };

  const handleResetSignature = (sigRole) => {
    setValue(`comments.${sigRole}.signature`, '', { shouldDirty: true, shouldValidate: true });
    setValue(`comments.${sigRole}.signatureType`, 'upload', { shouldDirty: true, shouldValidate: true });
  };

  const handleSaveSignature = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const signatureData = sigCanvas.current.toDataURL();
      setValue(`comments.${currentSignature.role}.signature`, signatureData, { shouldDirty: true, shouldValidate: true });
    }
    setShowModal(false);
    sigCanvas.current?.clear();
  };

  const handleClearSignature = () => {
    sigCanvas.current?.clear();
  };

  const handleCloseModal = () => {
    setShowModal(false);
    sigCanvas.current?.clear();
  };

  // ---------- Submit (single level, role-gated promotions) ----------
  const onSubmit = (action) => (data) => {
    // action: 'pending' (submit) or 'draft' (save) for eng/contractor; 'approve'/'rework' for manager/quality
    let newStatus = data.status;
    let manager_approve = data.manager_approve;

    const userRole = (role || cookie.get('user-role') || '').toLowerCase();

    if (userRole === 'engineer' && status() !== 'draft' && status() !== 'CONTRACTOR') return;
    if (userRole === 'contractor' && status() !== 'draft' && status() !== 'CONTRACTOR') return;

    if (userRole === 'engineer') {
      newStatus = 'CONTRACTOR';
    } else if (userRole === 'contractor') {
      newStatus = 'MANAGER';
    } else if (userRole === 'manager') {
      if (action === 'approve') {
        newStatus = 'QUALITY';
        manager_approve = true;
      } else if (action === 'rework' || action === 'draft') {
        newStatus = 'draft';
      } else {
        return;
      }
    } else if (userRole === 'quality') {
      if (action === 'approve') {
        newStatus = 'approved';
      } else if (action === 'rework' || action === 'draft') {
        newStatus = 'draft';
      } else {
        return;
      }
    } else {
      return;
    }

    setValue('status', newStatus);
    autoSave.cancel();

    const payload = {
      form_data: {
        status: newStatus,
        form_id: formId,
        formName: 'cabel_tray',
        station_id: data.station_id,
        project_name: data.project_name,
        cabel_tray_size: data.cabel_tray_size,
        no_of_trays: data.no_of_trays,
        cabel_tray_material: data.cabel_tray_material,
        cabel_tray_type: data.cabel_tray_type,
        visual_checks: data.visual_checks,
        other_comments: data.other_comments,
        comments: data.comments,
        observations: data.observations,
        manager_approve,
        punch_points: data.punch_points,
      },
    };

    dispatch({ type: SUBMIT_FORM, payload: { data: payload.form_data } });

    // Toasts mirroring APFC wording
    const msg =
      role === 'manager' || role === 'quality'
        ? action === 'approve' ? 'Submitted' : 'Saved as Draft'
        : (action === 'pending' ? 'Submitted' : 'Saved as Draft');

    toast.success(`${msg} Successfully`);
    navigate(-1);
  };

    const canEditPunchPoints = ['manager', 'quality', 'maintenance', 'operation'].includes((role || '').toLowerCase());
  const handleReworkClick = (levelOrAction, actionStr) => (e) => {
    if (e) e.preventDefault();
    if (actionStr) {
      setPendingRework({ level: levelOrAction, action: actionStr });
    } else {
      setPendingRework({ action: levelOrAction });
    }
    setShowPunchModal(true);
  };

  const handleSavePunchPoints = async () => {
    if (pendingRework) {
      setShowPunchModal(false);
      const currentValues = getValues();
      if (pendingRework.level) {
        onSubmit(pendingRework.level, pendingRework.action)(currentValues);
      } else {
        onSubmit(pendingRework.action)(currentValues);
      }
    }
  };

if (isLoading) return <div>Loading...</div>;

  // ---------- Signature cell ----------
  const renderSignatureCell = (sigRole, field) => {
    const editable = isSigEditable(sigRole);

    if (field === 'Date') {
      return <Form.Control type="date" {...register(`comments.${sigRole}.date`)} disabled={!editable} />;
    }
    if (field === 'Signature') {
      return (
        <div>
          <Form.Select
            {...register(`comments.${sigRole}.signatureType`)}
            onChange={(e) => handleSignatureTypeChange(sigRole, e.target.value)}
            className="mb-2"
            disabled={!editable}
          >
            <option value="upload">Image Upload</option>
            <option value="digital">Digital Sign</option>
          </Form.Select>

          {_.get(formValues, `comments.${sigRole}.signatureType`) === 'upload' && (
            <Form.Control
              type="file"
              accept="image/*"
              onChange={(e) => handleImageUpload(e, sigRole)}
              className="mb-2"
              disabled={!editable}
            />
          )}

          {_.get(formValues, `comments.${sigRole}.signature`) && (
            <div>
              <img
                src={_.get(formValues, `comments.${sigRole}.signature`)}
                alt="Signature"
                style={{ maxWidth: '100px', marginTop: '5px' }}
              />
              <Button
                variant="danger"
                size="sm"
                onClick={() => handleResetSignature(sigRole)}
                className="mt-2"
                disabled={!editable}
              >
                Reset Signature
              </Button>
            </div>
          )}
        </div>
      );
    }
    // Name/Company
    return (
      <Form.Control
        type="text"
        {...register(`comments.${sigRole}.${field.toLowerCase()}`)}
        disabled={!editable}
      />
    );
  };

  return (
    <div className="page-body container-xl">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="page-title mb-4">Cable Tray - Site Acceptance Testing</h2>
        <Button variant="outline-warning" size="sm" onClick={() => setShowPunchModal(true)}>
          💬 See Comments
        </Button>
      </div>

      {role === 'engineer' && status() !== 'draft' && (
        <div className="alert alert-warning mb-4">This form is in View Mode Only for Engineer.</div>
      )}

      <Form className="form-content-pane">
        {/* Equipment Details */}
        <h3>1. Equipment Details</h3>
        
        {punch_points && (
          <Alert variant="warning" className="d-flex align-items-center justify-content-between shadow-sm border-0 mb-4" style={{ borderRadius: '12px', background: '#fff9db', color: '#856404' }}>
            <div className="d-flex align-items-center">
              <IoInformationCircleOutline className="me-2 text-warning" size={24} />
              <div>
                <strong className="d-block">Rework Comments Available</strong>
                <span className="small">Please review the comments from the manager/quality team.</span>
              </div>
            </div>
            <Button 
              variant="warning" 
              size="sm" 
              className="rounded-pill px-3 fw-bold shadow-sm"
              onClick={() => setShowPunchModal(true)}
            >
              View Comments
            </Button>
          </Alert>
        )}

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Station ID</Form.Label>
              <Form.Control type="text" {...register('station_id')} readOnly />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Project Name</Form.Label>
              <Form.Control type="text" {...register('project_name')} disabled={!fieldsEditable} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Cable Tray Size (L x W x H)</Form.Label>
              <Form.Control type="text" {...register('cabel_tray_size')} disabled={!fieldsEditable} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>No. of Trays</Form.Label>
              <Form.Control type="text" {...register('no_of_trays')} disabled={!fieldsEditable} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Cable Tray Material</Form.Label>
              <Form.Select {...register('cabel_tray_material')} disabled={!fieldsEditable}>
                <option value="">Select</option>
                <option value="Aluminium">Aluminium</option>
                <option value="Fiberglass">Fiberglass</option>
                <option value="Galvanized Steel">Galvanized Steel</option>
                <option value="Stainless Steel">Stainless Steel</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Cable Tray Type</Form.Label>
              <Form.Select {...register('cabel_tray_type')} disabled={!fieldsEditable}>
                <option value="">Select</option>
                <option value="Perforated">Perforated</option>
                <option value="Ladder">Ladder</option>
                <option value="Solid Bottom">Solid Bottom</option>
                <option value="Wire Mesh">Wire Mesh</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {/* Visual Checks */}
        <h3 className="mt-4">2. Visual Checks</h3>
        <Table bordered>
          <thead>
            <tr><th>Sr. No.</th><th>Description</th><th>Status</th><th>Remark</th></tr>
          </thead>
          <tbody>
            {[
              'Is the packing list verified to ensure that materials match the approved material list?',
              'Have the materials been visually inspected for any dents or damage?',
              'Is the cable tray installation in accordance with the approved layout drawing?',
              'Are the size and length of the cable trays as per the approved layout drawing?',
              'Are the cable tray supports, and anchoring points properly installed?',
              'Is the correct type of cable tray installed as per the tray routing layout drawing?',
              'Are the trays and racks properly levelled and aligned, with expansion joints in place if specified?',
              'Are there no sharp edges along the entire cable tray routing?',
              'Is the gap between parallel runs sufficient to accommodate the required cable layers?',
              'Are there any sharp 90-degree bends in the cable trays?',
              'Are the cable trays correctly numbered according to the cable tray routing layout?',
              'Is earth continuity properly ensured between cable trays at the joints?',
              'Are earth jumper cables with crimp-type terminals installed at all cable tray joints?',
            ].map((desc, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>{desc}</td>
                <td>
                  <Form.Check
                    inline
                    label="Yes"
                    type="radio"
                    name={`visual_checks_${index}`}
                    value="Yes"
                    {...register(`visual_checks.${index}.status`)}
                    disabled={!fieldsEditable}
                  />
                  <Form.Check
                    inline
                    label="No"
                    type="radio"
                    name={`visual_checks_${index}`}
                    value="No"
                    {...register(`visual_checks.${index}.status`)}
                    disabled={!fieldsEditable}
                  />
                  <Form.Check
                    inline
                    label="NA"
                    type="radio"
                    name={`visual_checks_${index}`}
                    value="NA"
                    {...register(`visual_checks.${index}.status`)}
                    disabled={!fieldsEditable}
                  />
                </td>
                <td>
                  <Form.Control
                    type="text"
                    {...register(`visual_checks.${index}.remark`)}
                    disabled={!fieldsEditable}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {/* Observations */}
        <h3 className="mt-4">3. Observations during Commissioning / Testing (If Any)</h3>
        {['a', 'b', 'c'].map((k) => (
          <Form.Group className="mb-3" key={k}>
            <Form.Label>{k.toUpperCase()}.</Form.Label>
            <Form.Control type="text" {...register(`observations.${k}`)} disabled={!fieldsEditable} />
          </Form.Group>
        ))}

        {/* Other Comments */}
        <h3 className="mt-4">4. Other Comments / Remarks</h3>
        <Form.Group className="mb-3">
          <Form.Control as="textarea" rows={4} {...register('other_comments')} disabled={!canComment} />
        </Form.Group>

        {/* Comments & Signatures */}
        <h3 className="mt-4">5. Comments & Signatures</h3>
        <Table bordered>
          <thead>
            <tr>
              <th></th>
              <th>Engineer - Contractor</th>
              <th>Engineer - Charge Zone</th>
              <th>Quality - Charge Zone</th>
              <th>Optional</th>
            </tr>
          </thead>
          <tbody>
            {['Name', 'Company', 'Signature', 'Date'].map((field) => (
              <tr key={field}>
                <td>{field}</td>
                {['contractor', 'charge_engineer', 'quality', 'optional'].map((sigRole) => (
                  <td key={sigRole}>{renderSignatureCell(sigRole, field)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>

        {/* Actions (Single Level) */}
        {role === 'manager' && status() === 'MANAGER' ? (
          <div className="mt-4">
            <Button variant="success" onClick={handleSubmit(onSubmit('approve'))} className="me-2">
              Approve
            </Button>
            <Button variant="danger" onClick={handleSubmit(onSubmit('rework'))}>
              Re-Work
            </Button>
          </div>
        ) : role === 'quality' && status() === 'QUALITY' ? (
          <div className="mt-4">
            <Button variant="success" onClick={handleSubmit(onSubmit('approve'))} className="me-2">
              Approve
            </Button>
            <Button variant="danger" onClick={handleSubmit(onSubmit('rework'))}>
              Apply for Resubmit
            </Button>
          </div>
        ) : (
          <div className="mt-4">
            <Button
              variant="success"
              onClick={handleSubmit(onSubmit('pending'))}
              disabled={
                role === 'engineer' ? status() !== 'draft'
                : role === 'contractor' ? status() !== 'CONTRACTOR'
                : true
              }
            >
              Submit
            </Button>
            <Button
              variant="secondary"
              style={{ marginLeft: '10px' }}
              onClick={handleReworkClick('draft')}
              disabled={
                role === 'engineer' ? status() !== 'draft'
                : role === 'contractor' ? status() !== 'CONTRACTOR'
                : true
              }
            >
              Save As Draft
            </Button>
          </div>
        )}
      </Form>

      {/* Signature Modal */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Draw Signature</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <SignatureCanvas
            ref={sigCanvas}
            penColor="black"
            canvasProps={{ width: 500, height: 200, className: 'sigCanvas border' }}
          />
          <div className="mt-2">
            <Button variant="secondary" onClick={handleClearSignature} className="me-2">
              Clear
            </Button>
            <Button variant="primary" onClick={handleSaveSignature}>
              Save Signature
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    
      <PunchPointsModal
        show={showPunchModal}
        onHide={() => { setShowPunchModal(false); setPendingRework(null); }}
        value={punch_points}
        onChange={(val) => setValue('punch_points', val)}
        onSave={handleSavePunchPoints}
        canEdit={canEditPunchPoints && !!pendingRework}
      />
</div>
  );
};

CableTrayForm.propTypes = {
  role: PropTypes.oneOf(['engineer', 'contractor', 'manager', 'quality', 'admin']),
  userId: PropTypes.string,
};

export default CableTrayForm;
